import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { systemReminderService } from "../services/api";
import { Bell, Plus, Edit, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function SystemRemindersPage() {
  const { isAdmin } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", start_date: "", renewal_frequency: "annual", notes: "" });

  const loadReminders = async () => {
    try {
      const res = await systemReminderService.getAll();
      setReminders(res.data.items || []);
    } catch (error) {
      toast.error("Failed to load system reminders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReminders(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await systemReminderService.update(editing.id, form);
        toast.success("Reminder updated");
      } else {
        await systemReminderService.create(form);
        toast.success("Reminder created");
      }
      setShowModal(false);
      setEditing(null);
      setForm({ title: "", description: "", start_date: "", renewal_frequency: "annual", notes: "" });
      loadReminders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save reminder");
    }
  };

  const handleEdit = (r) => {
    setEditing(r);
    setForm({ title: r.title, description: r.description || "", start_date: r.start_date, renewal_frequency: r.renewal_frequency, notes: r.notes || "", status: r.status });
    setShowModal(true);
  };

  const handleDelete = async (rid) => {
    if (!window.confirm("Delete this system reminder?")) return;
    try {
      await systemReminderService.delete(rid);
      toast.success("Reminder deleted");
      loadReminders();
    } catch (error) {
      toast.error("Failed to delete reminder");
    }
  };

  const statusBadge = (status) => {
    const map = { overdue: "bg-red-100 text-red-700", due_soon: "bg-amber-100 text-amber-700", upcoming: "bg-blue-100 text-blue-700", ok: "bg-green-100 text-green-700" };
    const labels = { overdue: "Overdue", due_soon: "Due Soon", upcoming: "Upcoming", ok: "OK" };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || map.ok}`}>{labels[status] || status}</span>;
  };

  const freqLabel = (f) => ({ annual: "Annual", quarterly: "Quarterly", semi_annual: "Semi-Annual", every_3_years: "Every 3 Years", every_5_years: "Every 5 Years" }[f] || f);

  if (!isAdmin) {
    return (
      <div className="p-6 sm:p-8">
        <div className="enterprise-card p-8 text-center">
          <p className="text-slate-500">Only administrators can manage system reminders.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="h-48 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8" data-testid="system-reminders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">System Reminders</h1>
          <p className="text-sm text-slate-500 mt-1">Manage system-wide filing deadlines and renewal reminders</p>
        </div>
        <Button className="btn-primary" onClick={() => { setEditing(null); setForm({ title: "", description: "", start_date: "", renewal_frequency: "annual", notes: "" }); setShowModal(true); }} data-testid="add-system-reminder-btn">
          <Plus className="w-4 h-4 mr-1" />Add System Reminder
        </Button>
      </div>

      <div className="enterprise-card">
        {reminders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Frequency</th>
                  <th>Start Date</th>
                  <th>Next Due</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reminders.map((r) => (
                  <tr key={r.id} data-testid={`system-reminder-row-${r.id}`}>
                    <td className="font-medium text-slate-900">{r.title}</td>
                    <td className="text-slate-600 text-sm">{r.description || "-"}</td>
                    <td>{freqLabel(r.renewal_frequency)}</td>
                    <td>{r.start_date}</td>
                    <td className="font-medium">{r.next_due_date}</td>
                    <td>{statusBadge(r.computed_status)}</td>
                    <td className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(r)} data-testid={`edit-system-reminder-${r.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(r.id)} data-testid={`delete-system-reminder-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No system reminders</p>
            <p className="text-sm text-slate-400 mt-1">Add system-wide filing deadlines and important dates</p>
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit System Reminder" : "Add System Reminder"}</DialogTitle>
            <DialogDescription>System-wide reminders apply across all companies. Next due date is auto-calculated.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="e.g. Annual Tax Filing" className="form-input" required data-testid="system-reminder-title-input" />
              </div>
              <div>
                <Label className="form-label">Description</Label>
                <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Optional description" className="form-input" data-testid="system-reminder-description-input" />
              </div>
              <div>
                <Label className="form-label">Start Date *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} className="form-input" required data-testid="system-reminder-start-date-input" />
              </div>
              <div>
                <Label className="form-label">Renewal Frequency *</Label>
                <Select value={form.renewal_frequency} onValueChange={(v) => setForm({...form, renewal_frequency: v})}>
                  <SelectTrigger data-testid="system-reminder-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semi_annual">Semi-Annual</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="every_3_years">Every 3 Years</SelectItem>
                    <SelectItem value="every_5_years">Every 5 Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editing && (
                <div>
                  <Label className="form-label">Status</Label>
                  <Select value={form.status || "active"} onValueChange={(v) => setForm({...form, status: v})}>
                    <SelectTrigger data-testid="system-reminder-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="form-label">Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Additional notes" className="form-input" data-testid="system-reminder-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditing(null); }}>Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="save-system-reminder-btn">{editing ? "Update Reminder" : "Add Reminder"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
